import React, {useEffect, useRef} from 'react'
import styled from 'styled-components'
import {A11yAccordion} from '../../Atoms/Accordion'
import DocumentListItem, {NoDocumentsItem, NoDocumentsOrFoldersItem} from './Documents/DocumentListItem'
import {A11yIconListItem, A11yListContainer} from '../../Atoms/Lists'
import {IconFolder, IconFolderLinked} from '../../../Assets/Icons/LargeIcons'
import {IconDelete, IconEditNote} from '../../../Assets/Icons/TinyIcons'
import {openInNewTab} from '../../../config/utils'
import DoubleComponent from '../../Molecules/ListComponents/DoubleComponent'
import {Padding} from '../../Layout/Layout'
import {IconButton} from '../../Atoms/Buttons'
import {colors} from '../../../sharedComponents/colors'

import {DocumentGroup, Document, DocumentFolderType} from '../../../state/documents/state'

const FolderHeader = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  width: 100%;
  gap: 0.5rem;

  .icon {
    display: flex;
    margin-right: 0.5rem;

    svg {
      margin: auto;
      min-height: 40px;
      min-width: 40px;
    }
  }
`

const FolderEditButton = styled(IconButton)`
  opacity: 0.55;

  &:hover,
  &:focus {
    opacity: 0.85;
  }
`

const FolderActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

interface FolderAccordionProps {
  dg: DocumentGroup
  onClick?: (d: Document) => void
  onConfigure?: (id: string) => void
  canManageFolder?: (folder: DocumentGroup) => boolean
  canManageDocument?: (document: Document) => boolean
  onConfigureFolder?: (id: string) => void
  onDeleteFolder?: (folder: DocumentGroup) => void
  onDeleteDocument?: (document: Document) => void
  openDocumentId?: string | null
  openExternal: (dg: DocumentGroup) => void
  forceOpenState?: boolean
  useSettingsIcon?: boolean
}

const FolderAccordion = ({
  dg,
  onClick,
  onConfigure,
  canManageFolder,
  onConfigureFolder,
  onDeleteFolder,
  onDeleteDocument,
  canManageDocument,
  openDocumentId,
  openExternal,
  forceOpenState,
  useSettingsIcon
}: FolderAccordionProps) => {
  const isFolderTarget = !!openDocumentId && dg.id === openDocumentId
  const shouldOpen = !!openDocumentId && (dg.documents.some(doc => doc.id === openDocumentId) || isFolderTarget)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isFolderTarget && ref.current) {
      setTimeout(() => {
        const el = ref.current
        if (el) {
          try {
            el.scrollIntoView({block: 'center'})
          } catch (e) {
            el.scrollIntoView()
          }
        }
      }, 0)
    }
  }, [isFolderTarget])

  const handleConfigureFolder = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
    if (onConfigureFolder) {
      onConfigureFolder(dg.id)
    }
  }

  const handleDeleteFolder = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
    if (onDeleteFolder) {
      onDeleteFolder(dg)
    }
  }

  return (
    <div ref={ref}>
      <A11yAccordion
        title={dg.name}
        openByDefault={shouldOpen}
        forceOpenState={forceOpenState}
        onClick={dg.type === DocumentFolderType.External ? () => openExternal(dg) : undefined}
        description={dg.url || dg.description}
        header={
          <FolderHeader>
            <div className="icon">
              <IconFolder />
            </div>
            <DoubleComponent description={dg.url || dg.description} title={dg.name} />
            {canManageFolder?.(dg) && (onConfigureFolder || onDeleteFolder) ? (
              <FolderActionRow>
                {onConfigureFolder ? (
                  <FolderEditButton
                    buttonProps={{
                      onClick: handleConfigureFolder,
                      'aria-label': `Edit ${dg.name}`
                    }}
                    icon={<IconEditNote color={colors.brand.cobalt} width="30px" height="30px" />}
                  />
                ) : null}
                {onDeleteFolder ? (
                  <FolderEditButton
                    buttonProps={{
                      onClick: handleDeleteFolder,
                      'aria-label': `Delete ${dg.name}`
                    }}
                    icon={<IconDelete color={colors.brand.cobalt} width="26px" height="26px" />}
                  />
                ) : null}
              </FolderActionRow>
            ) : null}
          </FolderHeader>
        }
      >
        {dg.documents.length > 0 ? (
          dg.documents.map(d => (
            <DocumentListItem
              key={d.id}
              document={d}
              type={d.type}
              name={d.name}
              id={d.id}
              onClick={() => !!onClick && onClick(d)}
              onDelete={canManageDocument?.(d) ? onDeleteDocument : undefined}
              onConfigure={canManageDocument?.(d) ? onConfigure : undefined}
              isTarget={openDocumentId === d.id}
              useSettingsIcon={useSettingsIcon}
            />
          ))
        ) : (
          <NoDocumentsItem />
        )}
      </A11yAccordion>
    </div>
  )
}

interface DocumentAccordionProps {
  documentGroups: DocumentGroup[]
  onConfigure?: (id: string) => void
  canManageFolder?: (folder: DocumentGroup) => boolean
  canManageDocument?: (document: Document) => boolean
  onConfigureFolder?: (id: string) => void
  onDeleteFolder?: (folder: DocumentGroup) => void
  onDeleteDocument?: (document: Document) => void
  onClick?: (d: Document) => void
  openDocumentId?: string | null
  searchQuery?: string
  useSettingsIcon?: boolean
}
const DocumentList = ({
  documentGroups,
  onClick,
  onConfigure,
  canManageFolder,
  onConfigureFolder,
  onDeleteFolder,
  onDeleteDocument,
  canManageDocument,
  openDocumentId,
  searchQuery,
  useSettingsIcon
}: DocumentAccordionProps) => {
  const executeSearchQuery = (searchQuery: string): DocumentGroup[] => {
    return documentGroups
      .map(obj => {
        const isDocumentGroupNameQueried = obj.name.toLowerCase().includes(searchQuery.toLowerCase())

        if (searchQuery === '' || isDocumentGroupNameQueried) {
          return {
            type: obj.type,
            name: obj.name,
            id: obj.id,
            description: obj.description,
            url: obj.url,
            createdBy: obj.createdBy,
            documents: obj.documents
          }
        }

        const filteredDocuments = obj.documents.filter(doc =>
          doc.name.toLowerCase().includes(searchQuery.toLowerCase())
        )

        if (filteredDocuments.length > 0) {
          return {
            type: obj.type,
            name: obj.name,
            id: obj.id,
            description: obj.description,
            url: obj.url,
            createdBy: obj.createdBy,
            documents: filteredDocuments
          }
        }

        return null
      })
      .filter(Boolean) as DocumentGroup[]
  }

  const searchedDocumentGroups = !searchQuery ? documentGroups : executeSearchQuery(searchQuery)

  const openExternal = (dg: DocumentGroup) => {
    if (dg.url) {
      openInNewTab(dg.url)
    }
  }
  return (
    <A11yListContainer>
      {searchedDocumentGroups && searchedDocumentGroups.length > 0 ? (
        searchedDocumentGroups.map(dg =>
          dg.type === DocumentFolderType.External ? (
            <A11yIconListItem
              key={dg.id}
              icon={<IconFolderLinked />}
              onClick={() => openExternal(dg)}
              content={
                <Padding py=".75rem">
                  <DoubleComponent title={dg.name} description={dg.url || dg.description} />
                </Padding>
              }
              action={
                canManageFolder?.(dg) && (onConfigureFolder || onDeleteFolder) ? (
                  <FolderActionRow>
                    {onConfigureFolder ? (
                      <FolderEditButton
                        buttonProps={{
                          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onConfigureFolder(dg.id)
                          },
                          'aria-label': `Edit ${dg.name}`
                        }}
                        icon={<IconEditNote color={colors.brand.cobalt} width="30px" height="30px" />}
                      />
                    ) : null}
                    {onDeleteFolder ? (
                      <FolderEditButton
                        buttonProps={{
                          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onDeleteFolder(dg)
                          },
                          'aria-label': `Delete ${dg.name}`
                        }}
                        icon={<IconDelete color={colors.brand.cobalt} width="26px" height="26px" />}
                      />
                    ) : null}
                  </FolderActionRow>
                ) : (
                  undefined
                )
              }
            />
          ) : (
            <FolderAccordion
              key={dg.id}
              dg={dg}
              onClick={onClick}
              onConfigure={onConfigure}
              canManageDocument={canManageDocument}
              onConfigureFolder={onConfigureFolder}
              canManageFolder={canManageFolder}
              onDeleteFolder={onDeleteFolder}
              onDeleteDocument={onDeleteDocument}
              openDocumentId={openDocumentId}
              openExternal={openExternal}
              forceOpenState={searchQuery != null && searchQuery.trim() !== ''}
              useSettingsIcon={useSettingsIcon}
            />
          )
        )
      ) : (
        <NoDocumentsOrFoldersItem />
      )}
    </A11yListContainer>
  )
}

export default DocumentList
